import Store from '@ember-data/store';
import Controller from '@ember/controller';
import RouterService from '@ember/routing/router-service';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import * as WebAuthnJSON from '@github/webauthn-json/browser-ponyfill';
import { tracked } from '@glimmer/tracking';
import ENV from 'donor-elf-web/config/environment';
import { type LoginRouteModel } from 'donor-elf-web/routes/login';
import CurrentUser from 'donor-elf-web/services/current-user';
import type SessionService from 'donor-elf-web/services/session';
import Settings from 'donor-elf-web/services/settings';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';

type LoginStage = 'email' | 'password' | 'cant-find-email';

const emailValidation = {
  email: validatePresence({ presence: true, message: "Can't be blank" }),
};
const passwordValidation = {
  password: validatePresence({ presence: true, message: "Can't be blank" }),
};

export default class LoginController extends Controller {
  model!: LoginRouteModel;

  @service declare currentUser: CurrentUser;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked loading = false;
  @tracked stage: LoginStage = 'email';
  @tracked showValidationErrors = false;
  @tracked emailChangeset: any;
  @tracked email?: string;
  @tracked passwordChangeset: any;
  @tracked invalidPassword = false;
  @tracked error?: any;

  password?: any;

  get isYoungLifeEnvironment() {
    return ENV.apiUrl === 'https://younglife.donorelf.com';
  }

  templateDidInsert = async () => {
    this.createEmailChangeset();

    if (isPresent(this.emailChangeset.get('email'))) {
      await this.submitEmail();
    }
  };

  templateWillDestroy = () => {
    this.reset();
  };

  submitEmail = async () => {
    let redirecting = false;

    this.loading = true;

    try {
      await this.emailChangeset.validate();

      if (this.emailChangeset.get('isValid')) {
        this.email = this.emailChangeset.get('email');

        const records = (
          await this.store.query('unauthenticated/user-login-info', {
            filter: { email: this.emailChangeset.get('email') },
          })
        ).slice();

        if (records.length === 1) {
          const record = records[0]!;

          if (isPresent(record.foreignUserEnvironmentUrl)) {
            window.location.assign(
              `${record.foreignUserEnvironmentUrl}/web/login?loginEmail=${this.email}`,
            );
            redirecting = true;
          } else if (isPresent(record.ssoLoginUrl)) {
            window.location.assign(record.ssoLoginUrl!);
            redirecting = true;
          } else if (record.loginWithPasskey) {
            await this.loginWithPasskey(
              record.passkeyOptions,
              record.passkeyChallenge,
            );
          } else {
            this.createPasswordChangeset();
            this.stage = 'password';
          }
        } else {
          this.stage = 'cant-find-email';
        }
      } else {
        this.showValidationErrors = true;
      }
    } finally {
      if (!redirecting) {
        // leave the loading spinner up if we're redirecting
        this.loading = false;
      }
    }
  };

  submitPassword = async () => {
    this.loading = true;

    try {
      await this.passwordChangeset.validate();

      if (this.passwordChangeset.get('isValid')) {
        this.password = this.passwordChangeset.get('password');
        await this.loginWithEmailAndPassword();
      } else {
        this.showValidationErrors = true;
        this.invalidPassword = false;
      }
    } finally {
      this.loading = false;
    }
  };

  cancel = () => {
    this.reset({ createEmailChangeset: true });
  };

  async afterLogin(attemptedTransition?: PublicTransition | null) {
    await this.currentUser.load();

    if (attemptedTransition) {
      attemptedTransition.retry();
    } else {
      this.router.transitionTo('funds.fund', 'selected-fund');
    }
  }

  createEmailChangeset() {
    this.emailChangeset = Changeset(
      {
        email: this.model.email,
      },
      lookupValidator(emailValidation),
      emailValidation,
    );
  }

  createPasswordChangeset() {
    this.passwordChangeset = Changeset(
      {
        password: undefined,
      },
      lookupValidator(passwordValidation),
      passwordValidation,
    );
  }

  async loginWithEmailAndPassword() {
    this.invalidPassword = false;

    try {
      const attemptedTransition = this.session.attemptedTransition;
      await this.session.authenticate(
        'authenticator:oauth2',
        this.email,
        this.password,
        undefined,
        {
          'passkey-login': false,
        },
      );

      await this.afterLogin(attemptedTransition);
    } catch (error: any) {
      if (error.responseJSON?.error === 'Invalid Password') {
        this.invalidPassword = true;
      } else {
        this.error = error;
      }
    }
  }

  async loginWithPasskey(passkeyOptions: any, passkeyChallenge?: string) {
    this.invalidPassword = false;

    try {
      const options = WebAuthnJSON.parseRequestOptionsFromJSON({
        publicKey: passkeyOptions,
      });
      const passkeyCredential = await WebAuthnJSON.get(options);
      const attemptedTransition = this.session.attemptedTransition;

      await this.session.authenticate(
        'authenticator:oauth2',
        this.email,
        JSON.stringify({
          passkey_credential: passkeyCredential,
          passkey_challenge: passkeyChallenge,
        }),
        undefined,
        {
          'passkey-login': true,
        },
      );

      await this.afterLogin(attemptedTransition);
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        console.log('User canceled passkey login');
      } else {
        this.error = error;
      }
    }
  }

  reset({ createEmailChangeset = false } = {}) {
    if (createEmailChangeset) {
      this.createEmailChangeset();
    } else {
      this.emailChangeset = undefined;
    }

    this.loading = false;
    this.stage = 'email';
    this.showValidationErrors = false;
    this.email = undefined;
    this.passwordChangeset = undefined;
    this.password = undefined;
    this.invalidPassword = false;
    this.error = undefined;
  }
}
